import { api } from "@/services/api";
import {
  AddProduct,
  EditProduct,
  PaginatedResponse,
  Product,
  ProductFilters,
  ProductSummary,
  UpdateProductCategories,
  UpdateProductCountries,
  UpdateProductStock,
  UpdateProductSubCategories,
} from "@/types";

export class ProductRepository {
  async findSummary(id: string): Promise<ProductSummary> {
    const response = await api.get<ProductSummary>(`/v1/products/${id}`);

    return response.data;
  }

  async findAll(
    filters: ProductFilters = {},
  ): Promise<PaginatedResponse<Product>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      // Skip if value is null, undefined, empty string, or empty array
      if (this.shouldSkipParam(value)) {
        return;
      }

      // Handle array parameters (categories, subCategories, brands, countries, sources)
      if (this.isArrayParam(key)) {
        if (Array.isArray(value) && value.length > 0) {
          // Filter out empty strings from arrays
          const filteredArray = value.filter(
            (item) => item !== null && item !== undefined && item !== "",
          );

          if (filteredArray.length > 0) {
            filteredArray.forEach((val) => params.append(key, val));
          }
        }
      }
      // Handle tags parameter (comma-separated)
      else if (key === "tags") {
        if (Array.isArray(value) && value.length > 0) {
          const filteredTags = value.filter(
            (tag) => tag !== null && tag !== undefined && tag !== "",
          );

          if (filteredTags.length > 0) {
            params.append("tags", filteredTags.join(","));
          }
        }
      }
      // Handle numeric parameters (priceFrom, priceTo)
      else if (key === "priceFrom" || key === "priceTo") {
        const numValue = Number(value);

        if (!isNaN(numValue) && numValue > 0) {
          params.append(key, numValue.toString());
        }
      }
      // Handle sort parameter (array of {key, direction} objects)
      else if (key === "sort") {
        if (Array.isArray(value) && value.length > 0) {
          params.append(
            "sort",
            value
              .map(
                (s: { key: string; direction: string }) =>
                  `${s.key}:${s.direction}`,
              )
              .join(","),
          );
        }
      }
      // Handle boolean parameters (hasScrapedProduct, checkInStock)
      else if (key === "hasScrapedProduct" || key === "checkInStock") {
        if (typeof value === "boolean") {
          params.append(key, value.toString());
        }
      }
      // Handle string parameters
      else {
        const stringValue = String(value).trim();

        if (stringValue.length > 0) {
          params.append(key, stringValue);
        }
      }
    });

    const response = await api.get(`/v1/admin/products?${params.toString()}`);

    return response.data;
  }

  async add(data: AddProduct): Promise<Product> {
    const response = await api.post("/v1/admin/products", data);

    return response.data;
  }

  async update(data: EditProduct): Promise<Product> {
    const { id, ...payload } = data;
    const response = await api.patch(`/v1/admin/products/${id}`, payload);

    return response.data;
  }

  async updateStock(data: UpdateProductStock): Promise<any> {
    const { id, inStock } = data;
    const response = await api.patch(`/v1/admin/products/${id}/update-stock`, {
      inStock,
    });

    return response.data;
  }

  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/v1/admin/products/${id}`);

    return response.data;
  }

  async bulkDelete(
    ids: string[],
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(
      `/v1/admin/products/bulk-delete/?ids=${ids.join(",")}`,
    );

    return response.data;
  }

  async bulkUploadProducts(file: File): Promise<any> {
    const formData = new FormData();

    formData.append("file", file);

    const response = await api.post("/v1/admin/products/bulk", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data;
  }

  async updateCategories(
    productId: string,
    data: UpdateProductCategories,
  ): Promise<Product> {
    const response = await api.put(
      `/v1/admin/products/${productId}/categories`,
      data,
    );

    return response.data;
  }

  async updateSubCategories(
    productId: string,
    data: UpdateProductSubCategories,
  ): Promise<Product> {
    const response = await api.put(
      `/v1/admin/products/${productId}/sub-categories`,
      data,
    );

    return response.data;
  }

  async updateCountries(
    productId: string,
    data: UpdateProductCountries,
  ): Promise<Product> {
    const response = await api.put(
      `/v1/admin/products/${productId}/countries`,
      data,
    );

    return response.data;
  }

  // Helper method to check if a value should be skipped
  private shouldSkipParam(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    return false;
  }

  // Helper method to check if a parameter is an array parameter
  private isArrayParam(key: string): boolean {
    return [
      "categories",
      "subCategories",
      "brands",
      "countries",
      "sources",
    ].includes(key);
  }
}
